// Plot state helpers — reads/writes the active kingdom's `plots` map.
// Tiles are keyed by their axial hex key ("q,r"); every field tile has an
// entry (GRASS by default), so "owned vs for-sale" is gone — the player builds
// on grass via the BUILD menu instead.

import { PlotTypes, HomeLevels, RealEstatePrices, LandRingPrices, LandDemandSlope } from '../config/balance.js'
import { active, persist } from './state.js'
import { Events, emit } from '../ui/eventBus.js'
import { fieldCoords, key, axialToWorld, parseKey, hexDistance } from '../scene/hexGrid.js'

// Claimable tiles = whole field minus the home (center). Used for scarcity.
const CLAIMABLE_TILES = Math.max(1, fieldCoords().length - 1)

export function getPlot(tileKey) {
  return active().plots?.[tileKey] ?? null
}

// A tile is OWNED once it's been claimed — i.e. it has a plot whose type is
// anything except WILD (unowned wilderness). Missing entry = unowned WILD too.
export function isOwned(tileKey) {
  const t = active().plots?.[tileKey]?.type
  return !!t && t !== PlotTypes.WILD
}

export function isWild(tileKey) {
  return !isOwned(tileKey)
}

export function getPlotType(tileKey) {
  return active().plots?.[tileKey]?.type ?? PlotTypes.WILD
}

export function isFarmTile(tileKey) {
  return getPlotType(tileKey) === PlotTypes.FARM
}

export function isHomeTile(tileKey) {
  return getPlotType(tileKey) === PlotTypes.HOME
}

// World-space {x, z} of a tile, from its axial hex key.
export function tileToWorld(tileKey) {
  const { q, r } = parseKey(tileKey)
  return axialToWorld(q, r)
}

// The player's home tile key in the active kingdom (or null).
export function getHomeTileIndex() {
  const plots = active().plots ?? {}
  for (const [k, plot] of Object.entries(plots)) {
    if (plot?.type === PlotTypes.HOME) return k
  }
  return null
}

// ── Home tier (CR-5) ────────────────────────────────────────────────────────
// The home is a LEVEL on the home tile: SHACK (free default) → MIDDLE (manor,
// bought once). Stored on the home plot entry; defaults to SHACK.
export function getHomeLevel() {
  const hk = getHomeTileIndex()
  return (hk && active().plots?.[hk]?.homeLevel) || HomeLevels.SHACK
}

export function setHomeLevel(level) {
  const hk = getHomeTileIndex()
  if (!hk) return
  const k = active()
  k.plots[hk] = { ...k.plots[hk], homeLevel: level }
  persist()
  emit(Events.HomeLevelChange, { tileIndex: hk, level })
}

// Buy the in-place home upgrade (SHACK → MIDDLE manor). Returns false if already
// upgraded or not enough coin. spendWealth is injected to avoid a circular import.
export function upgradeHome(spendWealth) {
  if (getHomeLevel() !== HomeLevels.SHACK) return { ok: false, reason: 'already upgraded' }
  const cost = RealEstatePrices.HOME_MIDDLE
  if (!spendWealth(cost)) return { ok: false, reason: 'not enough coin' }
  setHomeLevel(HomeLevels.MIDDLE)
  return { ok: true, cost }
}

// Set / change a tile's plot type (e.g. building placement).
export function setPlot(tileKey, type) {
  const k = active()
  if (!k.plots) k.plots = {}
  k.plots[tileKey] = { ...(k.plots[tileKey] ?? {}), type }
  persist()
  emit(Events.PlotChanged, { tileIndex: tileKey, type })
}

// ── CR-9 land market: ring-based base price × scarcity premium ──────────────
// How many claimable tiles the player already owns (excludes WILD + HOME).
export function claimedCount() {
  const plots = active().plots ?? {}
  let n = 0
  for (const k of Object.keys(plots)) {
    const t = plots[k]?.type
    if (t && t !== PlotTypes.WILD && t !== PlotTypes.HOME) n += 1
  }
  return n
}
export function claimedFraction() {
  return Math.min(1, claimedCount() / CLAIMABLE_TILES)
}

// Price to claim a specific wild tile: cheaper toward the edges (high ring),
// dearer adjoining the home (ring 1), all rising as the field fills (scarcity).
export function plotPrice(tileKey) {
  const { q, r } = parseKey(tileKey)
  const ring = Math.max(1, hexDistance({ q, r }, { q: 0, r: 0 }))
  const base = LandRingPrices[ring] ?? LandRingPrices[6]
  const mult = 1 + LandDemandSlope * claimedFraction()
  return Math.round((base * mult) / 100) * 100 // tidy to the nearest 100
}

// Owned, undeveloped plots (cleared GRASS with no zone/building yet) — the
// inventory the BUILD panel lets you drop buildings onto.
export function countUndevelopedPlots() {
  return countByType(PlotTypes.GRASS)
}

// Claim a wild tile → owned GRASS. spendWealth injected (avoids a circular import).
export function claimPlot(tileKey, spendWealth) {
  if (isOwned(tileKey)) return { ok: false, reason: 'already owned' }
  const cost = plotPrice(tileKey)
  if (!spendWealth(cost)) return { ok: false, reason: 'not enough coin', cost }
  setPlot(tileKey, PlotTypes.GRASS)
  return { ok: true, cost }
}

// Count tiles of a given plot type in the active kingdom.
export function countByType(plotType) {
  const plots = active().plots ?? {}
  let n = 0
  for (const k of Object.keys(plots)) {
    if (plots[k]?.type === plotType) n += 1
  }
  return n
}

// Iterate every hex tile in the field with its plot. Any coord missing from the
// map is unowned WILD (the default state — players claim tiles to expand).
export function listAllTiles() {
  const plots = active().plots ?? {}
  const out = []
  for (const { q, r } of fieldCoords()) {
    const k = key(q, r)
    out.push({ tileIndex: k, plot: plots[k] ?? { type: PlotTypes.WILD } })
  }
  return out
}
